import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_ENDPOINTS, apiRequest } from '../Email-Marketing/config/api.js';

// Replaces the Email-Marketing `useAudienceStore` zustand store.
// The backend resolves source tables itself, so no table list is sent.

export const fetchAudienceContacts = createAsyncThunk(
  'emailAudience/fetchContacts',
  async ({ audienceData, limit = 1000, offset = 0 }, { rejectWithValue }) => {
    try {
      const data = await apiRequest(API_ENDPOINTS.AUDIENCE.CONTACTS, {
        method: 'POST',
        body: JSON.stringify({ ...audienceData, limit, offset }),
      });
      return data.data;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const getAudienceCount = createAsyncThunk(
  'emailAudience/getCount',
  async (audienceData) => {
    try {
      const data = await apiRequest(API_ENDPOINTS.AUDIENCE.COUNT, {
        method: 'POST',
        body: JSON.stringify(audienceData),
      });
      return data.data.count;
    } catch (error) {
      console.error('Error getting audience count:', error);
      return 0;
    }
  }
);

export const previewAudience = createAsyncThunk(
  'emailAudience/preview',
  async (audienceData) => {
    const data = await apiRequest(API_ENDPOINTS.AUDIENCE.PREVIEW, {
      method: 'POST',
      body: JSON.stringify(audienceData),
    });
    return data.data;
  }
);

export const getAllAudienceContacts = createAsyncThunk(
  'emailAudience/getAllContacts',
  async ({ limit = 1000, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('limit', limit.toString());
      queryParams.append('offset', offset.toString());
      const data = await apiRequest(`${API_ENDPOINTS.AUDIENCE.ALL}?${queryParams}`);
      return data.data;
    } catch (error) {
      console.error('Error fetching all contacts:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTableStats = createAsyncThunk(
  'emailAudience/fetchTableStats',
  async () => {
    try {
      const data = await apiRequest(API_ENDPOINTS.AUDIENCE.TABLE_STATS);
      return data.data;
    } catch (error) {
      console.error('Error fetching table stats:', error);
      return {};
    }
  }
);

export const fetchAvailableTables = createAsyncThunk(
  'emailAudience/fetchAvailableTables',
  async () => {
    try {
      const data = await apiRequest(API_ENDPOINTS.AUDIENCE.TABLES);
      return data.data;
    } catch (error) {
      console.error('Error fetching available tables:', error);
      return [];
    }
  }
);

export const getTableFields = createAsyncThunk(
  'emailAudience/getTableFields',
  async (tableName) => {
    try {
      const data = await apiRequest(API_ENDPOINTS.AUDIENCE.TABLE_FIELDS(tableName));
      return data.data;
    } catch (error) {
      console.error('Error fetching table fields:', error);
      return [];
    }
  }
);

export const getOverviewStats = createAsyncThunk(
  'emailAudience/getOverviewStats',
  async () => {
    const data = await apiRequest(API_ENDPOINTS.AUDIENCE.STATS);
    return data.data;
  }
);

const initialState = {
  contacts: [],
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  loading: false,
  error: null,
  tableStats: {},
  availableTables: [],
};

const emailAudienceSlice = createSlice({
  name: 'emailAudience',
  initialState,
  reducers: {
    clearAudienceError: (state) => {
      state.error = null;
    },
    resetAudience: () => initialState,
  },
  extraReducers: (builder) => {
    const listPending = (state) => {
      state.loading = true;
      state.error = null;
    };
    const listFulfilled = (state, action) => {
      state.loading = false;
      state.contacts = action.payload.contacts;
      state.totalCount = action.payload.totalCount;
      state.currentPage = action.payload.currentPage;
      state.totalPages = action.payload.totalPages;
    };
    const listRejected = (state, action) => {
      state.loading = false;
      state.error = action.payload;
    };
    builder
      .addCase(fetchAudienceContacts.pending, listPending)
      .addCase(fetchAudienceContacts.fulfilled, listFulfilled)
      .addCase(fetchAudienceContacts.rejected, listRejected)
      .addCase(getAllAudienceContacts.pending, listPending)
      .addCase(getAllAudienceContacts.fulfilled, listFulfilled)
      .addCase(getAllAudienceContacts.rejected, listRejected)
      .addCase(fetchTableStats.fulfilled, (state, action) => {
        state.tableStats = action.payload;
      })
      .addCase(fetchAvailableTables.fulfilled, (state, action) => {
        state.availableTables = action.payload;
      });
  },
});

export const { clearAudienceError, resetAudience } = emailAudienceSlice.actions;

export default emailAudienceSlice.reducer;
